import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import supportApi from '../../../services/supportApi';

const PRIORITY_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting: 'Waiting on User',
  resolved: 'Resolved',
  closed: 'Closed',
};

const STATUS_VALUES = ['open', 'in_progress', 'waiting', 'resolved', 'closed'];
const PRIORITY_VALUES = ['low', 'medium', 'high', 'urgent'];

function formatDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function SupportTicketsPage() {
  const { refreshing } = useOutletContext() || {};

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ status: '', category: '', priority: '' });

  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [replying, setReplying] = useState(false);
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await supportApi.adminGetTickets(filters);
      setTickets(Array.isArray(data?.tickets) ? data.tickets : []);
    } catch (err) {
      setError(err.message || 'Failed to load tickets.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const loadDetail = useCallback(async (ticketId) => {
    if (!ticketId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    setDetailError('');
    try {
      const data = await supportApi.adminGetTicketDetail(ticketId);
      setDetail(data);
    } catch (err) {
      setDetailError(err.message || 'Failed to load ticket.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleReply = async (event) => {
    event.preventDefault();
    if (!replyMessage.trim() || !selectedId || replying) return;
    setReplying(true);
    setActionError('');
    setNotice('');
    try {
      await supportApi.adminReplyToTicket(selectedId, replyMessage.trim());
      setReplyMessage('');
      setNotice('Reply sent. User has been notified by email.');
      await loadDetail(selectedId);
      await loadTickets();
    } catch (err) {
      setActionError(err.message || 'Failed to send reply.');
    } finally {
      setReplying(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedId) return;
    setActionError('');
    setNotice('');
    try {
      await supportApi.adminUpdateStatus(selectedId, newStatus);
      setNotice(`Status updated to ${STATUS_LABELS[newStatus] || newStatus}.`);
      await loadDetail(selectedId);
      await loadTickets();
    } catch (err) {
      setActionError(err.message || 'Failed to update status.');
    }
  };

  const handlePriorityChange = async (newPriority) => {
    if (!selectedId) return;
    setActionError('');
    setNotice('');
    try {
      await supportApi.adminUpdatePriority(selectedId, newPriority);
      setNotice(`Priority updated to ${PRIORITY_LABELS[newPriority] || newPriority}.`);
      await loadDetail(selectedId);
      await loadTickets();
    } catch (err) {
      setActionError(err.message || 'Failed to update priority.');
    }
  };

  const counts = useMemo(() => {
    const by = { open: 0, in_progress: 0, waiting: 0, resolved: 0, closed: 0 };
    tickets.forEach((t) => { by[t.status] = (by[t.status] || 0) + 1; });
    return by;
  }, [tickets]);

  return (
    <>
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Support Tickets</h1>
          <p className="admin-dashboard__subtitle">
            Triage user-submitted tickets. Replies email the user and move the ticket to &quot;Waiting&quot;.
          </p>
        </div>
        <button
          type="button"
          className="admin-dashboard__refresh"
          onClick={loadTickets}
          disabled={loading || refreshing}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </header>

      {notice && <div className="admin-dashboard__msg admin-dashboard__msg--success">{notice}</div>}
      {(error || actionError) && (
        <div className="admin-dashboard__msg admin-dashboard__msg--error">{error || actionError}</div>
      )}

      <div className="admin-support__toolbar">
        <div className="admin-support__filters">
          <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
            <option value="">All statuses</option>
            {STATUS_VALUES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]} {counts[s] ? `(${counts[s]})` : ''}
              </option>
            ))}
          </select>
          <select value={filters.priority} onChange={(e) => handleFilterChange('priority', e.target.value)}>
            <option value="">All priorities</option>
            {PRIORITY_VALUES.map((p) => (
              <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
            ))}
          </select>
        </div>
        <span className="admin-support__count">{tickets.length} tickets</span>
      </div>

      <div className="admin-support__layout">
        <div className="admin-support__list admin-dashboard__card">
          {loading ? (
            <div className="admin-dashboard__empty">Loading tickets...</div>
          ) : tickets.length === 0 ? (
            <div className="admin-dashboard__empty">No tickets match the current filters.</div>
          ) : (
            <ul className="admin-support__rows">
              {tickets.map((ticket) => (
                <li key={ticket.id}>
                  <button
                    type="button"
                    className={`admin-support__row${selectedId === ticket.id ? ' admin-support__row--active' : ''}`}
                    onClick={() => setSelectedId(ticket.id)}
                  >
                    <div className="admin-support__row-main">
                      <div className="admin-support__row-head">
                        <span className="admin-support__ticket-number">{ticket.ticketNumber}</span>
                        <span className="admin-support__row-date">{formatDate(ticket.createdAt)}</span>
                      </div>
                      <div className="admin-support__row-subject">{ticket.subject}</div>
                      <div className="admin-support__row-from">{ticket.userEmail || ticket.userUsername || '—'}</div>
                    </div>
                    <div className="admin-support__row-badges">
                      <span className={`admin-support__badge admin-support__badge--priority-${ticket.priority}`}>
                        {PRIORITY_LABELS[ticket.priority] || ticket.priority}
                      </span>
                      <span className={`admin-support__badge admin-support__badge--status-${ticket.status}`}>
                        {STATUS_LABELS[ticket.status] || ticket.status}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <aside className="admin-support__detail admin-dashboard__card">
          {!selectedId ? (
            <div className="admin-dashboard__empty">Select a ticket to view the conversation.</div>
          ) : detailLoading ? (
            <div className="admin-dashboard__empty">Loading ticket...</div>
          ) : detailError ? (
            <div className="admin-dashboard__msg admin-dashboard__msg--error">{detailError}</div>
          ) : detail ? (
            <>
              <div className="admin-support__detail-header">
                <div>
                  <div className="admin-support__ticket-number">{detail.ticket.ticketNumber}</div>
                  <div className="admin-support__detail-subject">{detail.ticket.subject}</div>
                </div>
                <button
                  type="button"
                  className="admin-support__close"
                  onClick={() => setSelectedId('')}
                  aria-label="Close ticket detail"
                >
                  ×
                </button>
              </div>

              <div className="admin-support__detail-meta">
                <div><strong>From:</strong> {detail.ticket.userEmail || detail.ticket.userUsername || '—'}</div>
                <div><strong>Category:</strong> {detail.ticket.categoryLabel}</div>
                <div><strong>Opened:</strong> {formatDate(detail.ticket.createdAt)}</div>
              </div>

              <div className="admin-support__controls">
                <label>
                  Status
                  <select
                    value={detail.ticket.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                  >
                    {STATUS_VALUES.map((s) => (
                      <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Priority
                  <select
                    value={detail.ticket.priority}
                    onChange={(e) => handlePriorityChange(e.target.value)}
                  >
                    {PRIORITY_VALUES.map((p) => (
                      <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="admin-support__thread">
                <div className="admin-support__bubble admin-support__bubble--user">
                  <div className="admin-support__bubble-head">
                    {detail.ticket.userEmail || 'User'} — {formatDate(detail.ticket.createdAt)}
                  </div>
                  <div className="admin-support__bubble-body">{detail.ticket.message}</div>
                  {detail.ticket.attachmentUrl && (
                    <a href={detail.ticket.attachmentUrl} target="_blank" rel="noreferrer" className="admin-support__attachment">
                      View attachment
                    </a>
                  )}
                </div>

                {(detail.replies || []).map((reply) => (
                  <div
                    key={reply.id}
                    className={`admin-support__bubble admin-support__bubble--${reply.authorType === 'support' ? 'support' : 'user'}`}
                  >
                    <div className="admin-support__bubble-head">
                      {reply.authorType === 'support' ? 'Support' : 'User'} — {formatDate(reply.createdAt)}
                    </div>
                    <div className="admin-support__bubble-body">{reply.message}</div>
                  </div>
                ))}
              </div>

              <form className="admin-support__reply" onSubmit={handleReply}>
                <textarea
                  rows={4}
                  placeholder="Reply to the user..."
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                />
                <button
                  type="submit"
                  className="admin-dashboard__action-btn admin-dashboard__action-btn--approve"
                  disabled={!replyMessage.trim() || replying}
                >
                  {replying ? 'Sending...' : 'Send reply'}
                </button>
              </form>
            </>
          ) : null}
        </aside>
      </div>
    </>
  );
}

export default SupportTicketsPage;
